import {
  addHours,
  differenceInDays,
  format,
  formatISO,
  isAfter,
  isBefore,
  isValid as isValidDate,
  parse,
  parseISO,
} from '@e3dc-react/shell/Libs/date'
import { logError } from '../../utils'
import { getDateTimeLocalFormat } from '../../utils/date-locale-formats'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'

/**
 * Module for all date related features, parsers and transformers.
 */

/**
 * Formats tariff "from" time from DTO value.
 * @param tariffFrom a time string in the format H:m (Examples 3:12, 14:5, 20:10)
 * @returns an ISO string
 */
export const getTariffFromTime = (tariffFrom: string): string => {
  const time = formatISO(parse(tariffFrom, 'H:m', new Date()))
  return time
}

/**
 * Formats ISO date for tariff POST DTO.
 * @param tariffFromISO ISO date string
 * @returns the date formatted as HH:mm
 */
export const getTariffFromTimeToDto = (tariffFromISO: string): string => {
  return format(parseISO(tariffFromISO), 'HH:mm')
}

/**
 * Formats daytime from ModbusTCP evse DTO.
 * @param daytime the number of seconds since the beginning of the day (as a string)
 * @returns an ISO string
 */
export const getModbusTcpDepartureTime = (daytime: string): string => {
  const seconds = parseInt(daytime) || 0
  const hours = Math.floor(seconds / (60 * 60))
  const minutes = Math.floor(seconds / 60) % 60

  const departureTime = formatISO(parse(`${hours}:${minutes}`, 'H:m', new Date()))

  return departureTime
}
/**
 * Formats ISO date for Charging station evse POST or PUT DTO.
 * @param tariffFromISO ISO date string
 * @returns the number of seconds since the beinning of the day (as a string)
 */
export const getChargingStationDepartureTimeToDto = (departureTime: string): string => {
  const date = parseISO(departureTime)

  if (!isValidDate(date)) {
    logError(`Function getModbusTcpDepartureTimeToDto received an invalid ISO date. ${departureTime}`)
    return '0'
  }

  const hours = date.getHours()
  const minutes = date.getMinutes()
  const seconds = hours * 60 * 60 + minutes * 60
  return seconds.toString()
}

/**
 * Formats daytime from MyPV I/O boost config DTO.
 * @param time a time string in the format H:mm (Examples 3:12, 4:05, 20:10)
 * @returns a date
 */
export const getMyPvIoBoostTimeFromDto = (time: string): Date => {
  const date = parse(time, 'H:mm', new Date())

  if (!isValidDate(date)) {
    logError(`Function getMyPvIoTimeFromDto was given an invalid value "${time}".
      Expected a value of time in the format of H:mm`)
    return new Date()
  }

  return date
}
/**
 * Formats daytime for MyPV I/O boost POST or PUT config DTO.
 * @param time time as a date
 * @returns a time string in the format H:mm (Examples 3:12, 4:05, 20:10)
 */
export const getMyPvIoBoostTimeToDto = (time: Date): string => {
  if (!isValidDate(time)) {
    logError(`Function getMyPvIoBoostTimeToDto was given an invalid value "${time}".
      Expected an ISO date string.`)
    return '0:00'
  }

  return format(time, 'H:mm')
}

/**
 * @param createdAt ISO date string
 * @returns the date formatted in the common format with 48 hours added
 */
export const getInstallationInvitationDate = (createdAt: string, language: AppLang): string => {
  const date = parseISO(createdAt)

  if (!isValidDate(date)) {
    logError(`Function getInstallationInvitationDate was given an invalid value.`)
    return ''
  }

  const languageFormat = getDateTimeLocalFormat(language)

  return format(addHours(date, 48), languageFormat)
}

/**
 * Utility to get ISO times
 * @param time a string representing time formatted as HH:mm (Examples 06:01, 12:00, 00:59)
 */
export const getTimeAsISO = (time: string): string => {
  const date = parse(time, 'HH:mm', new Date())

  if (!isValidDate(date)) {
    logError(`Function getTimeAsISO was given an invalid value.`)
    return formatISO(parse('00:00', 'HH:mm', new Date()))
  }

  return formatISO(date)
}

/**
 * Utility to get a date
 * @param time a string representing time formatted as HH:mm (Examples 06:01, 12:00, 00:59)
 */
export const getTimeAsDate = (time: string): Date => {
  const date = parse(time, 'HH:mm', new Date())

  if (!isValidDate(date)) {
    logError(`Function getTimeAsISO was given an invalid value.`)
    return parse('00:00', 'HH:mm', new Date())
  }

  return date
}

/**
 * Get the number of minutes since the begining of a day.
 * @param date ISO date string
 */
export const getMinutesFromDate = (date: string): number => {
  const dateTime = parseISO(date)

  if (!isValidDate(dateTime)) {
    logError(`Function getMinutesFromDate was given an invalid ISO date string.`)
    return 0
  }

  const hour = dateTime.getHours()
  const minutes = dateTime.getMinutes()
  return hour * 60 + minutes
}

/**
 * If any date is invalid, the result will be false.
 *
 * @param dateX date
 * @param dateY date
 * @returns true if date X is before date Y, false if X is equal or after Y
 */
export const isDateXBeforeDateY = (dateX: Date, dateY: Date): boolean => {
  if (!isValidDate(dateX) || !isValidDate(dateY)) return false

  return isBefore(dateX, dateY)
}

/**
 * If any date is invalid, the result will be false.
 *
 * @param dateX ISO date string
 * @param dateY ISO date string
 * @returns true if date X is after date Y, false if X is equal or before Y
 */
export const isDateXAfterDateY = (dateX: string, dateY: string): boolean => {
  const dateTimeX = parseISO(dateX)
  const dateTimeY = parseISO(dateY)
  if (!isValidDate(dateTimeX) || !isValidDate(dateTimeY)) return false

  return isAfter(dateTimeX, dateTimeY)
}

/**
 * @param x1 ms
 * @param x2 ms
 * @param y1 ms
 * @param y2 ms
 * @returns true if the date ranges overlap
 * Example:
 * - [01:00 ~ 03:15] and [03:00 ~ 12:00] overlap
 * - [13:00 ~ 14:15] and [14:16 ~ 14:17] don't overlap
 */
export const getTimeRangesOverlap = (x1: number, x2: number, y1: number, y2: number): boolean => {
  const [xStart, xEnd] = sortTuple(x1, x2)
  const [yStart, yEnd] = sortTuple(y1, y2)
  return xStart <= yEnd && xEnd >= yStart
}

/** Given two number, it returns them lowest to highest as a tuple. */
const sortTuple = (x: number, y: number): [number, number] => {
  if (x < y) return [x, y]
  return [y, x]
}

/**
 * Given a date, it returns the time of that date in milliseconds.
 * If the date is invalid, it returns null.
 * @param date Date
 */
export const getTimeFromDate = (date: Date): number | null => {
  if (!isValidDate(date)) return null
  const hours = date.getHours()
  const minutes = date.getMinutes()
  return 1000 * 60 * 60 * hours + 1000 * 60 * minutes
}

/**
 * @param date ISO date string
 * @returns the time in the format HH:mm
 */
export const formatIsoToHours = (date: string): string => {
  const dateTime = parseISO(date)

  if (!isValidDate(dateTime)) {
    logError(`Function formatIsoToHours was given an invalid ISO date string.`)
    return '00:00'
  }

  return format(dateTime, 'HH:mm')
}

/**
 * @param date Date
 * @returns the time in the format HH:mm string
 */
export const formatDateToHours = (date: Date): string => {
  if (!isValidDate(date)) {
    logError(`Function formatIsoToHours was given an invalid ISO date string.`)
    return '00:00'
  }

  return format(date, 'HH:mm')
}

/**
 * @param date ISO date string.
 * @returns the amount of days from today until the date
 * If the date if before today or the date is invalid, it returns 0.
 */
export const getDaysUntilIsoDate = (date: string): number => {
  const dateTime = parseISO(date)

  if (!isValidDate(dateTime)) {
    logError(`Function getDaysUntilTodayFromIsoDate was given an invalid ISO date string.`)
    return 0
  }

  const today = new Date()

  if (!isAfter(dateTime, today)) return 0

  const diff = differenceInDays(dateTime, today)

  return Math.ceil(diff)
}

/**
 * Translates ISO date to its format for a repective language .
 * Returns empty string for invalid dates.
 * Does not log an error if date is an empty string and just returns an empty string.
 *
 * @param date ISO data
 * @param language
 */
export const formatIsoDateTimeForLanguage = (date: string, language: AppLang): string => {
  if (!date) return ''

  const dateTime = parseISO(date)

  if (!isValidDate(dateTime)) {
    logError(`Function formatIsoDateForLanguage was given an invalid ISO date string.`)
    return ''
  }

  return format(dateTime, getDateTimeLocalFormat(language))
}

/**
 * Translates milliseconds to their datetime format for a repective language.
 * Returns empty string for invalid dates.
 *
 * @param date ISO data
 * @param language
 */
export const formatMillisecondsDateTimeForLanguage = (ms: number, language: AppLang): string => {
  const dateTime = new Date(ms)

  if (!isValidDate(dateTime)) {
    logError(`Function formatMillisecondsDateTimeForLanguage was given an invalid input.`)
    return ''
  }

  return format(dateTime, getDateTimeLocalFormat(language))
}

export const checkIfIsBetween = (startAt: string | null, stopAt: string | null, from: Date, to: Date): boolean => {
  if (!startAt) return false

  const startDate = parseISO(startAt)
  const stopDate = stopAt ? parseISO(stopAt) : new Date()
  return isBefore(from, stopDate) && isAfter(to, startDate)
}
